// Using browser's native WebSocket
declare const WebSocket: {
  new (url: string): WebSocket;
  CONNECTING: number;
  OPEN: number;
  CLOSING: number;
  CLOSED: number;
}

export interface ExchangeConfig {
  name: string;
  baseUrl: string;
  apiKey?: string;
  apiSecret?: string;
  rateLimit: number; // requests per second
}

export interface ConnectionState {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastConnected?: Date;
  lastError?: string;
  retryCount: number;
  latency?: number;
  heartbeatMisses?: number;
  lastHeartbeat?: Date;
}

export interface PriceData {
  symbol: string;
  price: number;
  timestamp: number;
  exchange: string;
  volume?: number;
  change24h?: number;
}

export interface WebSocketMessage {
  type: 'price' | 'trade' | 'orderbook' | 'heartbeat';
  data: any;
  timestamp: number;
  exchange: string;
}

class WebSocketService {
  private connections: Map<string, WebSocket> = new Map()
  private connectionStates: Map<string, ConnectionState> = new Map()
  private messageHandlers: Map<string, (message: WebSocketMessage) => void> =
    new Map()
  private reconnectTimers: Map<string, number> = new Map()

  // Exchange configurations
  private readonly exchangeConfigs: Record<string, ExchangeConfig> = {
    binance: {
      name: 'Binance',
      baseUrl: 'wss://stream.binance.com:9443/stream',
      rateLimit: 1200
    },
    coinbase: {
      name: 'Coinbase',
      baseUrl: 'wss://ws-feed.pro.coinbase.com',
      rateLimit: 30
    }
  }

  // Firi uses REST API instead of WebSocket
  private readonly firiConfig = {
    name: 'Firi',
    baseUrl: '/api/trade/prices/nok',
    rateLimit: 10 // Lower rate limit for REST API
  }

  // Exchange priority system for failover
  private readonly exchangePriority = ['binance', 'coinbase', 'firi']
  private readonly exchangeFailover: Map<string, string[]> = new Map()

  // Price cache for fallback during outages
  private readonly priceCache: Map<string, PriceData> = new Map()
  private readonly cacheExpiry: Map<string, number> = new Map()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

  // User notification system
  private readonly statusChangeHandlers: Map<
    string,
    (status: ConnectionState, exchange: string, symbol: string) => void
  > = new Map()
  private readonly notificationHandlers: Map<
    string,
    (message: string, type: 'info' | 'warning' | 'error' | 'success') => void
  > = new Map()

  // Heartbeat system for connection health monitoring
  private readonly heartbeatIntervals: Map<string, number> = new Map()
  private readonly heartbeatTimeouts: Map<string, number> = new Map()
  private readonly HEARTBEAT_INTERVAL = 30000 // 30 seconds
  private readonly HEARTBEAT_TIMEOUT = 10000 // 10 seconds
  private readonly MAX_HEARTBEAT_MISSES = 3
  private readonly EXCHANGES_WITH_HEARTBEAT = ['coinbase'] // Only Coinbase uses heartbeat

  /**
   * Connect to a specific exchange for a coin pair
   */
  async connect(exchange: string, symbol: string): Promise<void> {
    const connectionKey = `${exchange}:${symbol}`

    // Check if connection already exists
    if (this.connections.has(connectionKey)) {
      return
    }

    // Handle Firi differently since it uses REST API polling
    if (exchange.toLowerCase() === 'firi') {
      await this.setupFiriPolling(symbol)
      return
    }

    const config = this.exchangeConfigs[exchange]
    if (!config) {
      throw new Error(`Exchange ${exchange} not configured`)
    }

    try {
      const ws = await this.createConnection(exchange, symbol, config)
      this.connections.set(connectionKey, ws)
    } catch (error) {
      console.error(`Failed to connect to ${exchange} for ${symbol}:`, error)
      throw error
    }
  }

  /**
   * Connect with automatic failover to next priority exchange
   */
  async connectWithFailover(symbol: string): Promise<void> {
    for (const exchange of this.exchangePriority) {
      try {
        await this.connect(exchange, symbol)

        return
      } catch (_error) {
        console.warn(
          `Failed to connect to ${exchange} for ${symbol}, trying next exchange`
        )
        continue
      }
    }

    throw new Error(`Failed to connect to any exchange for ${symbol}`)
  }

  /**
   * Handle exchange failover when primary connection fails
   */
  private async handleExchangeFailover(
    failedExchange: string,
    symbol: string
  ): Promise<void> {
    const currentIndex = this.exchangePriority.indexOf(failedExchange)
    if (
      currentIndex === -1 ||
      currentIndex === this.exchangePriority.length - 1
    ) {
      return
    }

    const nextExchange = this.exchangePriority[currentIndex + 1]
    if (!nextExchange) {
      return
    }

    // Attempting failover from failedExchange to nextExchange for symbol

    // Notify about failover attempt
    this.notifyUser(
      `Attempting failover from ${failedExchange} to ${nextExchange} for ${symbol}`,
      'warning'
    )

    try {
      await this.connect(nextExchange, symbol)
      // Successfully failed over to nextExchange for symbol
      this.notifyUser(
        `Successfully failed over to ${nextExchange} for ${symbol}`,
        'success'
      )
    } catch (error) {
      console.error(`Failover to ${nextExchange} failed for ${symbol}:`, error)
      this.notifyUser(
        `Failover to ${nextExchange} failed for ${symbol}`,
        'error'
      )
      // Continue failover chain
      await this.handleExchangeFailover(nextExchange, symbol)
    }
  }

  /**
   * Create WebSocket connection for a specific exchange and symbol
   */
  private async createConnection(
    exchange: string,
    symbol: string,
    config: ExchangeConfig
  ): Promise<WebSocket> {
    const connectionKey = `${exchange}:${symbol}`

    // Update connection state
    this.connectionStates.set(connectionKey, {
      status: 'connecting',
      retryCount: 0
    })

    // Update store connection status
    await this.updateStoreConnectionStatus()

    try {
      let ws: WebSocket

      if (exchange === 'binance') {
        // Use the working Binance pattern from integration.js
        ws = new WebSocket(config.baseUrl)

        ws.onopen = () => {
          // Update connection state
          const state = this.connectionStates.get(connectionKey)
          if (state) {
            state.status = 'connected'
            state.lastConnected = new Date()
          }

          this.updateStoreConnectionStatus()

          // Subscribe to the symbol
          this.sendSubscriptionMessage(ws, exchange, symbol)

          // Start heartbeat
          this.startHeartbeat(connectionKey)
        }

        ws.onmessage = (event) => {
          try {
            const _data = JSON.parse(event.data)
            this.handleMessage(exchange, symbol, event.data)
          } catch (error) {
            console.error(
              `Error parsing message from ${exchange}:${symbol}:`,
              error
            )
          }
        }

        ws.onerror = (_error) => {
          console.error(`WebSocket error for ${exchange}:${symbol}:`, _error)
          this.handleConnectionError(exchange, symbol, _error)
          this.updateStoreConnectionStatus()
        }

        ws.onclose = (event) => {
          // WebSocket closed for binance exchange:symbol
          this.handleConnectionClose(exchange, symbol, event)
          this.updateStoreConnectionStatus()
        }
      } else if (exchange === 'coinbase') {
        // Coinbase connection logic
        ws = new WebSocket(config.baseUrl)

        ws.onopen = () => {
          // Update connection state
          const state = this.connectionStates.get(connectionKey)
          if (state) {
            state.status = 'connected'
            state.lastConnected = new Date()
          }

          this.updateStoreConnectionStatus()

          // Subscribe to the symbol
          this.sendSubscriptionMessage(ws, exchange, symbol)

          // Start heartbeat
          this.startHeartbeat(connectionKey)
        }

        ws.onmessage = (event) => {
          try {
            const _data = JSON.parse(event.data)
            this.handleMessage(exchange, symbol, event.data)
          } catch (error) {
            console.error(
              `Error parsing message from ${exchange}:${symbol}:`,
              error
            )
          }
        }

        ws.onerror = (_error) => {
          console.error(`WebSocket error for ${exchange}:${symbol}:`, _error)
          this.handleConnectionError(exchange, symbol, _error)
          this.updateStoreConnectionStatus()
        }

        ws.onclose = (event) => {
          // WebSocket closed for coinbase exchange:symbol
          this.handleConnectionClose(exchange, symbol, event)
          this.updateStoreConnectionStatus()
        }
      } else {
        throw new Error(`Unsupported exchange: ${exchange}`)
      }

      // Store the connection
      this.connections.set(connectionKey, ws)

      return ws
    } catch (error) {
      console.error(
        `Failed to create connection for ${exchange}:${symbol}:`,
        error
      )

      // Update connection state
      const state = this.connectionStates.get(connectionKey)
      if (state) {
        state.status = 'error'
        state.lastError = (error as Error).message ?? String(error)
      }

      throw error
    }
  }

  /**
   * Send exchange-specific subscription messages after connection
   */
  private sendSubscriptionMessage(
    ws: WebSocket,
    exchange: string,
    symbol: string
  ): void {
    if (exchange === 'binance') {
      // Use the working Binance subscription pattern from integration.js
      ws.send(
        JSON.stringify({
          method: 'SUBSCRIBE',
          params: [`${symbol.toLowerCase()}@trade`],
          id: Date.now()
        })
      )
    } else if (exchange === 'coinbase') {
      // Coinbase subscription
      ws.send(
        JSON.stringify({
          type: 'subscribe',
          product_ids: [symbol],
          channels: ['ticker']
        })
      )
    }
  }

  /**
   * Setup polling for Firi REST API since it doesn't support WebSocket
   */
  private async setupFiriPolling(symbol: string): Promise<void> {
    const connectionKey = `firi:${symbol}`

    // Initialize connection state for Firi
    this.connectionStates.set(connectionKey, {
      status: 'connected',
      retryCount: 0,
      lastConnected: new Date()
    })

    // Start polling for Firi prices
    this.startFiriPolling(symbol)

    // Setup polling for Firi symbol
  }

  /**
   * Start polling Firi REST API for price updates
   */
  private startFiriPolling(_symbol: string): void {
    // Check if Firi polling is already active
    const existingInterval = this.reconnectTimers.get('firi:polling')
    if (existingInterval) {
      // Firi polling is already active, just register this symbol
      return
    }

    const pollInterval = 30000 // Poll every 30 seconds instead of 5 seconds

    const poll = async () => {
      try {
        // Get authentication headers
        const { headers } = await useApiHeaders(true)

        const response = await $fetch(this.firiConfig.baseUrl, {
          headers
        })

        // Process all Firi symbols at once
        if (Array.isArray(response)) {
          response.forEach((item: any) => {
            if (item.id && item.last) {
              const priceData = {
                symbol: item.id,
                price: parseFloat(item.last) || 0,
                timestamp: Date.now(),
                exchange: 'firi'
              }

              // Create a WebSocket-like message for consistency
              const message: WebSocketMessage = {
                type: 'price',
                data: priceData,
                timestamp: Date.now(),
                exchange: 'firi'
              }

              // Call registered message handlers for this symbol
              const handler = this.messageHandlers.get(`firi:${item.id}`)
              if (handler) {
                handler(message)
              }
            }
          })
        }

        // Update connection state for all Firi connections
        this.connectionStates.forEach((state, key) => {
          if (key.startsWith('firi:')) {
            state.lastConnected = new Date()
            state.status = 'connected'
          }
        })
      } catch (error) {
        console.error(`Error polling Firi:`, error)

        // Update connection state on error
        this.connectionStates.forEach((state, key) => {
          if (key.startsWith('firi:')) {
            state.status = 'error'
            state.lastError =
              error instanceof Error ? error.message : 'Polling error'
          }
        })
      }
    }

    // Start polling
    poll()
    const intervalId = setInterval(poll, pollInterval)

    // Store interval ID for cleanup (use a single key for all Firi polling)
    this.reconnectTimers.set('firi:polling', intervalId as any)
  }

  /**
   * Parse Firi REST API response
   */
  private parseFiriResponse(response: any, symbol: string): any {
    // Parse the response based on the actual API structure
    // The /api/trade/prices/nok endpoint returns an array of price objects
    if (Array.isArray(response)) {
      const priceItem = response.find((item: any) => item.id === symbol)
      if (priceItem) {
        return {
          symbol: priceItem.id,
          price: parseFloat(priceItem.last) || 0,
          timestamp: Date.now(),
          exchange: 'firi'
        }
      }
    }

    // Fallback if symbol not found or response format is different
    return {
      symbol,
      price: 0,
      timestamp: Date.now(),
      exchange: 'firi'
    }
  }

  /**
   * Build WebSocket URL based on exchange requirements
   */
  private buildWebSocketUrl(
    exchange: string,
    symbol: string,
    config: ExchangeConfig
  ): string {
    switch (exchange) {
      case 'binance':
        // Binance uses lowercase symbols with @ticker for price updates
        return `${config.baseUrl}/${symbol.toLowerCase()}@ticker`

      case 'coinbase':
        // Coinbase uses product IDs and requires subscription message
        return config.baseUrl

      default:
        throw new Error(`Unsupported exchange: ${exchange}`)
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private async handleMessage(
    exchange: string,
    symbol: string,
    data: string
  ): Promise<void> {
    try {
      const parsed = JSON.parse(data)
      // WebSocketService: Received message from exchange:symbol

      // Handle Binance stream format (from integration.js)
      if (exchange === 'binance') {
        // Handle subscription responses (these don't have stream/data structure)
        if (parsed.result !== undefined) {
          // WebSocketService: Subscription response for exchange:symbol
          return
        }

        // Handle stream data
        if (parsed.stream && parsed.data) {
          const streamData = parsed.data
          // WebSocketService: Stream data for exchange:symbol

          if (streamData.e === 'trade') {
            const priceData: PriceData = {
              symbol: streamData.s,
              exchange: 'binance',
              price: parseFloat(streamData.p),
              timestamp: streamData.T,
              volume: parseFloat(streamData.q)
            }

            // WebSocketService: Processed price data for exchange:symbol

            // Cache the price data
            this.cachePriceData(symbol, exchange, priceData)

            // Directly update the price store
            try {
              const { useRealTimePriceStore } = await import(
                '~/stores/RealTimePriceStore'
              )
              const priceStore = useRealTimePriceStore()
              priceStore.updatePrice(priceData)
              // WebSocketService: Directly updated price store for exchange:symbol
            } catch (error) {
              console.error(
                `WebSocketService: Failed to update price store for ${exchange}:${symbol}:`,
                error
              )
            }

            // Call registered message handlers
            const handler = this.messageHandlers.get(`${exchange}:${symbol}`)
            if (handler) {
              // WebSocketService: Calling handler for exchange:symbol
              handler({
                type: 'price',
                data: priceData,
                timestamp: Date.now(),
                exchange
              })
            }
            // No handler found for exchange:symbol - skip
          }
        }
      } else if (exchange === 'coinbase') {
        // Handle Coinbase message format
        if (parsed.type === 'ticker') {
          const priceData: PriceData = {
            symbol: parsed.product_id,
            exchange: 'coinbase',
            price: parseFloat(parsed.price),
            timestamp: Date.now(),
            volume: parseFloat(parsed.volume_24h)
          }

          // WebSocketService: Processed price data for exchange:symbol

          // Cache the price data
          this.cachePriceData(symbol, exchange, priceData)

          // Directly update the price store
          try {
            const { useRealTimePriceStore } = await import(
              '~/stores/RealTimePriceStore'
            )
            const priceStore = useRealTimePriceStore()
            priceStore.updatePrice(priceData)
            // WebSocketService: Directly updated price store for exchange:symbol
          } catch (error) {
            console.error(
              `WebSocketService: Failed to update price store for ${exchange}:${symbol}:`,
              error
            )
          }

          // Call registered message handlers
          const handler = this.messageHandlers.get(`${exchange}:${symbol}`)
          if (handler) {
            // WebSocketService: Calling handler for exchange:symbol
            handler({
              type: 'price',
              data: priceData,
              timestamp: Date.now(),
              exchange
            })
          }
          // No handler found for exchange:symbol - skip
        }
      }
    } catch (error) {
      console.error(`Error parsing message from ${exchange}:${symbol}:`, error)
    }
  }

  /**
   * Process exchange-specific message formats
   */
  private processExchangeMessage(
    exchange: string,
    data: any,
    symbol: string
  ): any {
    switch (exchange) {
      case 'binance':
        return this.processBinanceMessage(data, symbol)

      case 'coinbase':
        return this.processCoinbaseMessage(data, symbol)

      default:
        return data
    }
  }

  /**
   * Process Binance WebSocket messages
   */
  private processBinanceMessage(data: any, _symbol: string): any {
    // Binance sends different message types
    if (data.e === '24hrTicker') {
      return {
        symbol: data.s,
        price: parseFloat(data.c), // Current price
        priceChange: parseFloat(data.P), // Price change percentage
        volume: parseFloat(data.v), // Volume
        high24h: parseFloat(data.h), // 24h high
        low24h: parseFloat(data.l), // 24h low
        timestamp: data.E, // Event time
        exchange: 'binance'
      }
    }

    return null // Skip other message types
  }

  /**
   * Process Coinbase WebSocket messages
   */
  private processCoinbaseMessage(data: any, symbol: string): any {
    // Coinbase sends different message types
    if (data.type === 'ticker') {
      return {
        symbol: data.product_id,
        price: parseFloat(data.price),
        priceChange: parseFloat(data.price_24h_change || '0'),
        volume: parseFloat(data.volume_24h || '0'),
        high24h: parseFloat(data.high_24h || '0'),
        low24h: parseFloat(data.low_24h || '0'),
        timestamp: new Date(data.time).getTime(),
        exchange: 'coinbase'
      }
    } else if (data.type === 'pong') {
      // Handle heartbeat response
      const connectionKey = `coinbase:${symbol}`
      this.handleHeartbeatResponse(connectionKey)
      return null // Don't process as price data
    }

    return null // Skip other message types
  }

  /**
   * Determine message type based on exchange and data structure
   */
  private determineMessageType(
    exchange: string,
    data: any
  ): WebSocketMessage['type'] {
    switch (exchange) {
      case 'binance':
        return data.e === '24hrTicker' ? 'price' : 'trade'

      case 'coinbase':
        return data.type === 'ticker' ? 'price' : 'trade'

      case 'firi':
        return data.type === 'price' ? 'price' : 'trade'

      default:
        return 'trade'
    }
  }

  /**
   * Handle connection errors
   */
  private handleConnectionError(
    exchange: string,
    symbol: string,
    _error: Event
  ): void {
    const connectionKey = `${exchange}:${symbol}`
    const state = this.connectionStates.get(connectionKey)

    if (state) {
      state.status = 'error'
      state.lastError = 'Connection error'

      // Notify about connection error
      this.notifyStatusChange(state, exchange, symbol)
      this.notifyUser(`Connection error for ${exchange} ${symbol}`, 'error')
    }

    // Trigger reconnection logic
    this.scheduleReconnect(exchange, symbol)
  }

  /**
   * Handle connection close
   */
  private handleConnectionClose(
    exchange: string,
    symbol: string,
    event: CloseEvent
  ): void {
    const connectionKey = `${exchange}:${symbol}`

    // Remove connection
    this.connections.delete(connectionKey)

    const state = this.connectionStates.get(connectionKey)
    if (state) {
      state.status = 'disconnected'

      // Notify about connection close
      this.notifyStatusChange(state, exchange, symbol)
      this.notifyUser(`Connection closed for ${exchange} ${symbol}`, 'warning')
    }

    // Clear any existing reconnect timer
    const timer = this.reconnectTimers.get(connectionKey)
    if (timer) {
      clearTimeout(timer)
      this.reconnectTimers.delete(connectionKey)
    }

    // Schedule reconnection if not a clean close
    if (event.code !== 1000) {
      this.scheduleReconnect(exchange, symbol)
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(exchange: string, symbol: string): void {
    const connectionKey = `${exchange}:${symbol}`
    const state = this.connectionStates.get(connectionKey)

    if (!state || state.retryCount >= 5) {
      // Max retries reached for connectionKey
      return
    }

    const backoffDelay = Math.min(1000 * Math.pow(2, state.retryCount), 16000)
    state.retryCount++

    // Scheduling reconnect for connectionKey with backoffDelay (attempt retryCount)

    // Notify about reconnection attempt
    this.notifyUser(
      `Reconnecting to ${exchange} ${symbol} in ${backoffDelay}ms (attempt ${state.retryCount})`,
      'info'
    )

    const timer = window.setTimeout(() => {
      this.reconnectTimers.delete(connectionKey)
      this.connect(exchange, symbol).catch(console.error)
    }, backoffDelay)

    this.reconnectTimers.set(connectionKey, timer)
  }

  /**
   * Disconnect from a specific exchange and symbol
   */
  disconnect(exchange: string, symbol: string): void {
    const connectionKey = `${exchange}:${symbol}`

    if (exchange === 'firi') {
      // For Firi, we don't clear the polling interval since it's shared
      // Just remove the connection state and message handler
      this.connectionStates.delete(connectionKey)
      this.messageHandlers.delete(connectionKey)

      // If no more Firi connections, stop the polling
      const firiConnections = Array.from(this.connectionStates.keys()).filter(key => key.startsWith('firi:'))
      if (firiConnections.length === 0) {
        const pollingInterval = this.reconnectTimers.get('firi:polling')
        if (pollingInterval) {
          clearInterval(pollingInterval as any)
          this.reconnectTimers.delete('firi:polling')
        }
      }
    } else {
      // Handle WebSocket connections
      const ws = this.connections.get(connectionKey)
      if (ws) {
        ws.close(1000, 'Manual disconnect')
        this.connections.delete(connectionKey)
      }

      // Clear reconnect timer
      const timer = this.reconnectTimers.get(connectionKey)
      if (timer) {
        clearTimeout(timer)
        this.reconnectTimers.delete(connectionKey)
      }
    }

    // Stop heartbeat monitoring
    this.stopHeartbeat(connectionKey)

    // Update state
    const state = this.connectionStates.get(connectionKey)
    if (state) {
      state.status = 'disconnected'
    }
  }

  /**
   * Register a message handler for a specific exchange and symbol
   */
  onMessage(
    exchange: string,
    symbol: string,
    handler: (message: WebSocketMessage) => void
  ): void {
    const key = `${exchange}:${symbol}`
    this.messageHandlers.set(key, handler)
  }

  /**
   * Remove message handler
   */
  offMessage(exchange: string, symbol: string): void {
    const key = `${exchange}:${symbol}`
    this.messageHandlers.delete(key)
  }

  /**
   * Get connection state for a specific exchange and symbol
   */
  getConnectionState(
    exchange: string,
    symbol: string
  ): ConnectionState | undefined {
    const key = `${exchange}:${symbol}`
    return this.connectionStates.get(key)
  }

  /**
   * Get all active connections
   */
  getActiveConnections(): string[] {
    return Array.from(this.connections.keys())
  }

  /**
   * Get available exchanges for manual selection
   */
  getAvailableExchanges(): string[] {
    return [...this.exchangePriority]
  }

  /**
   * Get current exchange for a symbol
   */
  getCurrentExchange(symbol: string): string | null {
    for (const exchange of this.exchangePriority) {
      const connectionKey = `${exchange}:${symbol}`
      const state = this.connectionStates.get(connectionKey)
      if (state && state.status === 'connected') {
        return exchange
      }
    }
    return null
  }

  /**
   * Manually switch to a specific exchange for a symbol
   */
  async switchExchange(symbol: string, targetExchange: string): Promise<void> {
    // Disconnect from current exchange
    const currentExchange = this.getCurrentExchange(symbol)
    if (currentExchange) {
      this.disconnect(currentExchange, symbol)
    }

    // Connect to target exchange
    await this.connect(targetExchange, symbol)
  }

  /**
   * Get connection status for all exchanges for a symbol
   */
  getExchangeStatus(symbol: string): Record<string, ConnectionState> {
    const status: Record<string, ConnectionState> = {}

    for (const exchange of this.exchangePriority) {
      const connectionKey = `${exchange}:${symbol}`
      const state = this.connectionStates.get(connectionKey)
      status[exchange] = state || {
        status: 'disconnected',
        retryCount: 0
      }
    }

    return status
  }

  /**
   * Cache price data for fallback during outages
   */
  private cachePriceData(
    symbol: string,
    exchange: string,
    priceData: PriceData
  ): void {
    const cacheKey = `${symbol}:${exchange}`
    this.priceCache.set(cacheKey, priceData)
    this.cacheExpiry.set(cacheKey, Date.now() + this.CACHE_DURATION)
  }

  /**
   * Get cached price data if available and not expired
   */
  getCachedPriceData(symbol: string, exchange: string): PriceData | null {
    const cacheKey = `${symbol}:${exchange}`
    const cachedData = this.priceCache.get(cacheKey)
    const expiryTime = this.cacheExpiry.get(cacheKey)

    if (!cachedData || !expiryTime || Date.now() > expiryTime) {
      // Remove expired cache entry
      this.priceCache.delete(cacheKey)
      this.cacheExpiry.delete(cacheKey)
      return null
    }

    return cachedData
  }

  /**
   * Get cached price data for any available exchange
   */
  getCachedPriceDataForSymbol(symbol: string): PriceData | null {
    for (const exchange of this.exchangePriority) {
      const cachedData = this.getCachedPriceData(symbol, exchange)
      if (cachedData) {
        return cachedData
      }
    }
    return null
  }

  /**
   * Clear expired cache entries
   */
  private cleanupExpiredCache(): void {
    const now = Date.now()
    for (const [cacheKey, expiryTime] of this.cacheExpiry) {
      if (now > expiryTime) {
        this.priceCache.delete(cacheKey)
        this.cacheExpiry.delete(cacheKey)
      }
    }
  }

  /**
   * Get fallback price data when all connections are down
   */
  getFallbackPriceData(symbol: string): PriceData | null {
    // First try to get cached data for the current exchange
    const currentExchange = this.getCurrentExchange(symbol)
    if (currentExchange) {
      const cachedData = this.getCachedPriceData(symbol, currentExchange)
      if (cachedData) {
        return cachedData
      }
    }

    // If no current exchange or cached data, try any available cached data
    return this.getCachedPriceDataForSymbol(symbol)
  }

  /**
   * Check if we have any cached price data for a symbol
   */
  hasCachedPriceData(symbol: string): boolean {
    return this.getCachedPriceDataForSymbol(symbol) !== null
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { totalEntries: number; expiredEntries: number } {
    this.cleanupExpiredCache()
    const totalEntries = this.priceCache.size
    const expiredEntries = this.cacheExpiry.size - totalEntries

    return {
      totalEntries,
      expiredEntries
    }
  }

  /**
   * Register a status change handler
   */
  onStatusChange(
    handler: (
      status: ConnectionState,
      exchange: string,
      symbol: string,
    ) => void
  ): string {
    const id = `status_${Date.now()}_${Math.random()}`
    this.statusChangeHandlers.set(id, handler)
    return id
  }

  /**
   * Remove a status change handler
   */
  offStatusChange(handlerId: string): void {
    this.statusChangeHandlers.delete(handlerId)
  }

  /**
   * Register a notification handler
   */
  onNotification(
    handler: (
      message: string,
      type: 'info' | 'warning' | 'error' | 'success',
    ) => void
  ): string {
    const id = `notification_${Date.now()}_${Math.random()}`
    this.notificationHandlers.set(id, handler)
    return id
  }

  /**
   * Remove a notification handler
   */
  offNotification(handlerId: string): void {
    this.notificationHandlers.delete(handlerId)
  }

  /**
   * Notify all registered handlers about status changes
   */
  private notifyStatusChange(
    status: ConnectionState,
    exchange: string,
    symbol: string
  ): void {
    for (const handler of this.statusChangeHandlers.values()) {
      try {
        handler(status, exchange, symbol)
      } catch (error) {
        console.error('Error in status change handler:', error)
      }
    }
  }

  /**
   * Notify all registered handlers about general notifications
   */
  private notifyUser(
    message: string,
    type: 'info' | 'warning' | 'error' | 'success'
  ): void {
    for (const handler of this.notificationHandlers.values()) {
      try {
        handler(message, type)
      } catch (error) {
        console.error('Error in notification handler:', error)
      }
    }
  }

  /**
   * Disconnect all connections
   */
  disconnectAll(): void {
    // Notify about service shutdown
    this.notifyUser('Disconnecting all WebSocket connections', 'info')

    // Close all WebSocket connections
    for (const [_key, ws] of this.connections) {
      ws.close(1000, 'Service shutdown')
    }
    this.connections.clear()

    // Clear all timers and intervals
    for (const [_key, timer] of this.reconnectTimers) {
      if (_key.startsWith('firi:')) {
        clearInterval(timer as any)
      } else {
        clearTimeout(timer)
      }
    }
    this.reconnectTimers.clear()
    this.connectionStates.clear()

    // Clear heartbeat timers
    for (const [_key, timer] of this.heartbeatIntervals) {
      clearInterval(timer)
    }
    this.heartbeatIntervals.clear()

    for (const [_key, timer] of this.heartbeatTimeouts) {
      clearTimeout(timer)
    }
    this.heartbeatTimeouts.clear()

    // Notify about successful shutdown
    this.notifyUser('All WebSocket connections disconnected', 'success')
  }

  /**
   * Start heartbeat monitoring for a connection
   */
  private startHeartbeat(connectionKey: string): void {
    const [exchange] = connectionKey.split(':')
    if (!exchange || !this.EXCHANGES_WITH_HEARTBEAT.includes(exchange)) {
      return // Only start heartbeat for supported exchanges
    }

    // Clear any existing heartbeat
    this.stopHeartbeat(connectionKey)

    const interval = window.setInterval(() => {
      this.sendHeartbeat(connectionKey)
    }, this.HEARTBEAT_INTERVAL)

    this.heartbeatIntervals.set(connectionKey, interval)
  }

  /**
   * Stop heartbeat monitoring for a connection
   */
  private stopHeartbeat(connectionKey: string): void {
    const interval = this.heartbeatIntervals.get(connectionKey)
    if (interval) {
      clearInterval(interval)
      this.heartbeatIntervals.delete(connectionKey)
    }

    const timeout = this.heartbeatTimeouts.get(connectionKey)
    if (timeout) {
      clearTimeout(timeout)
      this.heartbeatTimeouts.delete(connectionKey)
    }
  }

  /**
   * Send heartbeat message to exchange
   */
  private sendHeartbeat(connectionKey: string): void {
    const [exchange] = connectionKey.split(':')
    const ws = this.connections.get(connectionKey)

    if (!ws || ws.readyState !== 1) {
      // WebSocket.OPEN
      return
    }

    // Skip heartbeat for unsupported exchanges
    if (!exchange || !this.EXCHANGES_WITH_HEARTBEAT.includes(exchange)) {
      return
    }

    try {
      // Send exchange-specific heartbeat
      switch (exchange) {
        case 'coinbase':
          // Coinbase supports ping/pong
          ws.send(JSON.stringify({ type: 'ping' }))
          break

        default:
          // For other exchanges, just check connection state
          break
      }

      // Set timeout for heartbeat response
      const timeout = window.setTimeout(() => {
        this.handleHeartbeatTimeout(connectionKey)
      }, this.HEARTBEAT_TIMEOUT)

      this.heartbeatTimeouts.set(connectionKey, timeout)
    } catch (error) {
      console.error(`Error sending heartbeat to ${connectionKey}:`, error)
      this.handleHeartbeatFailure(connectionKey)
    }
  }

  /**
   * Handle heartbeat timeout
   */
  private handleHeartbeatTimeout(connectionKey: string): void {
    const [_exchange, _symbol] = connectionKey.split(':')
    const state = this.connectionStates.get(connectionKey)

    if (!state) return

    // Increment missed heartbeats
    if (!state.heartbeatMisses) {
      state.heartbeatMisses = 0
    }
    state.heartbeatMisses++

    console.warn(
      `Heartbeat timeout for ${connectionKey} (missed: ${state.heartbeatMisses})`
    )

    if (state.heartbeatMisses >= this.MAX_HEARTBEAT_MISSES) {
      console.error(
        `Max heartbeat misses reached for ${connectionKey}, reconnecting`
      )
      this.handleHeartbeatFailure(connectionKey)
    } else {
      // Try another heartbeat
      this.sendHeartbeat(connectionKey)
    }
  }

  /**
   * Handle heartbeat failure
   */
  private handleHeartbeatFailure(connectionKey: string): void {
    const [exchange, symbol] = connectionKey.split(':')

    if (!exchange || !symbol) {
      console.error(`Invalid connection key format: ${connectionKey}`)
      return
    }

    console.error(
      `Heartbeat failure for ${connectionKey}, initiating reconnection`
    )

    // Notify about heartbeat failure
    this.notifyUser(
      `Connection health check failed for ${exchange} ${symbol}`,
      'warning'
    )

    // Disconnect and reconnect
    this.disconnect(exchange, symbol)
    this.connect(exchange, symbol).catch((error) => {
      console.error(
        `Failed to reconnect after heartbeat failure: ${connectionKey}`,
        error
      )
    })
  }

  /**
   * Handle heartbeat response
   */
  private handleHeartbeatResponse(connectionKey: string): void {
    const state = this.connectionStates.get(connectionKey)
    if (state) {
      state.heartbeatMisses = 0
      state.lastHeartbeat = new Date()
    }

    // Clear timeout
    const timeout = this.heartbeatTimeouts.get(connectionKey)
    if (timeout) {
      clearTimeout(timeout)
      this.heartbeatTimeouts.delete(connectionKey)
    }
  }

  /**
   * Get connection health metrics
   */
  getConnectionHealth(connectionKey: string): {
    isHealthy: boolean;
    lastHeartbeat?: Date;
    missedHeartbeats: number;
    latency?: number;
  } {
    const state = this.connectionStates.get(connectionKey)
    if (!state) {
      return { isHealthy: false, missedHeartbeats: 0 }
    }

    const missedHeartbeats = state.heartbeatMisses || 0
    const isHealthy = missedHeartbeats < this.MAX_HEARTBEAT_MISSES

    return {
      isHealthy,
      lastHeartbeat: state.lastHeartbeat,
      missedHeartbeats,
      latency: state.latency
    }
  }

  /**
   * Update the store's connection status based on current state
   */
  private async updateStoreConnectionStatus(): Promise<void> {
    try {
      const { useRealTimePriceStore } = await import(
        '~/stores/RealTimePriceStore'
      )
      const priceStore = useRealTimePriceStore()

      // Check if we have any connected WebSockets
      let hasConnected = false
      let hasConnecting = false

      for (const [key, state] of this.connectionStates) {
        const ws = this.connections.get(key)

        if (ws && ws.readyState === 1) {
          hasConnected = true
        } else if (state.status === 'connecting') {
          hasConnecting = true
        }
      }

      // Update store connection status
      if (hasConnected) {
        priceStore.setConnectionStatus('connected')
      } else if (hasConnecting) {
        priceStore.setConnectionStatus('connecting')
      } else {
        priceStore.setConnectionStatus('disconnected')
      }
    } catch (error) {
      console.error('Failed to update store connection status:', error)
    }
  }

  /**
   * Connect multiple symbols to a single WebSocket connection (optimized)
   */
  async connectMultipleSymbols(
    exchange: string,
    symbols: string[]
  ): Promise<void> {
    if (exchange === 'binance') {
      // Create a single connection for all Binance symbols
      const connectionKey = `${exchange}:consolidated`

      // Check if connection already exists
      if (this.connections.has(connectionKey)) {
        // Consolidated connection already exists for exchange
        return
      }

      const config = this.exchangeConfigs[exchange]
      if (!config) {
        throw new Error(`Exchange ${exchange} not configured`)
      }

      // Update connection state
      this.connectionStates.set(connectionKey, {
        status: 'connecting',
        retryCount: 0
      })

      await this.updateStoreConnectionStatus()

      try {
        const ws = new WebSocket(config.baseUrl)

        ws.onopen = () => {
          // Update connection state
          const state = this.connectionStates.get(connectionKey)
          if (state) {
            state.status = 'connected'
            state.lastConnected = new Date()
          }

          this.updateStoreConnectionStatus()

          // Subscribe to all symbols in one message
          this.sendConsolidatedSubscriptionMessage(ws, exchange, symbols)

          // Start heartbeat
          this.startHeartbeat(connectionKey)
        }

        ws.onmessage = (event) => {
          try {
            this.handleConsolidatedMessage(exchange, event.data)
          } catch (error) {
            console.error(
              `Error parsing consolidated message from ${exchange}:`,
              error
            )
          }
        }

        ws.onerror = (error) => {
          console.error(`Consolidated WebSocket error for ${exchange}:`, error)
          this.handleConnectionError(exchange, 'consolidated', error)
          this.updateStoreConnectionStatus()
        }

        ws.onclose = (event) => {
          // Consolidated WebSocket closed for exchange
          this.handleConnectionClose(exchange, 'consolidated', event)
          this.updateStoreConnectionStatus()
        }

        // Store the consolidated connection
        this.connections.set(connectionKey, ws)
      } catch (error) {
        console.error(
          `Failed to create consolidated connection for ${exchange}:`,
          error
        )
        throw error
      }
    } else {
      // For other exchanges, fall back to individual connections
      for (const symbol of symbols) {
        await this.connect(exchange, symbol)
      }
    }
  }

  /**
   * Send subscription message for multiple symbols
   */
  private sendConsolidatedSubscriptionMessage(
    ws: WebSocket,
    exchange: string,
    symbols: string[]
  ): void {
    if (exchange === 'binance') {
      // Subscribe to all symbols in one message
      const params = symbols.map((symbol) => `${symbol.toLowerCase()}@ticker`)

      ws.send(
        JSON.stringify({
          method: 'SUBSCRIBE',
          params: params,
          id: Date.now()
        })
      )

      // Subscribed to symbols on consolidated exchange connection
    }
  }

  /**
   * Handle messages from consolidated connection
   */
  private async handleConsolidatedMessage(
    exchange: string,
    data: string
  ): Promise<void> {
    try {
      const parsedData = JSON.parse(data)

      if (exchange === 'binance') {
        // Handle Binance stream data
        if (parsedData.stream) {
          const symbol = parsedData.stream.replace('@ticker', '').toUpperCase()
          const priceData = this.processBinanceMessage(parsedData.data, symbol)

          if (priceData) {
            // Update price store
            try {
              const { useRealTimePriceStore } = await import(
                '~/stores/RealTimePriceStore'
              )
              const priceStore = useRealTimePriceStore()
              priceStore.updatePrice(priceData)
              // Consolidated WebSocket: Updated price for symbol
            } catch (error) {
              console.error(
                `Failed to update price store for ${symbol}:`,
                error
              )
            }
          }
        }
      }
    } catch (error) {
      console.error(
        `Error handling consolidated message from ${exchange}:`,
        error
      )
    }
  }

  /**
   * Disconnect all existing connections and create consolidated ones
   */
  async optimizeConnections(): Promise<void> {
    // Optimizing WebSocket connections

    // Get all current connections
    const _currentConnections = Array.from(this.connections.keys())
    // Current connections: _currentConnections.length

    // Disconnect all existing connections
    this.disconnectAll()

    // Wait a moment for connections to close
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // All connections disconnected, ready for optimization
  }

  /**
   * Get connection statistics
   */
  getConnectionStats(): {
    totalConnections: number;
    activeConnections: number;
    consolidatedConnections: number;
    individualConnections: number;
    } {
    const totalConnections = this.connections.size
    const activeConnections = Array.from(this.connections.values()).filter(
      (ws) => ws.readyState === 1
    ).length
    const consolidatedConnections = Array.from(this.connections.keys()).filter(
      (key) => key.includes('consolidated')
    ).length
    const individualConnections = totalConnections - consolidatedConnections

    return {
      totalConnections,
      activeConnections,
      consolidatedConnections,
      individualConnections
    }
  }
}

// Export singleton instance only on client side
let webSocketService: WebSocketService | null = null

if (process.client) {
  webSocketService = new WebSocketService()
}

export { webSocketService }
export default webSocketService
